import { useLinkProps } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { EventCard } from "@whoosh/design-system/src/apps/PatronApp/screens/multiSlotBooking/components/EventCard/EventCard";
import { formatHHMM } from "@whoosh/utilities/src/dates";

import { matchEventStatus } from "../../../../../../../Home/components/FeaturedEvents/EventsList";
import { getAgendaEventNodeKeyBySessionIdAtom } from "../../atoms/agendaEventNodesByIdAtom/agendaEventNodesByIdAtom";
import { EventSlot_eventSession$key } from "./__generated__/EventSlot_eventSession.graphql";

type EventSlotStatusInput = Parameters<typeof matchEventStatus>[0];

export const getEventSlotEventStatus = ({
  eventSignUpStatus,
  signUpStatusForClient,
  accessStatusForClient,
}: EventSlotStatusInput): ReturnType<typeof matchEventStatus> | undefined => {
  if (eventSignUpStatus === "DRAFT") {
    return undefined;
  }

  return matchEventStatus({
    eventSignUpStatus,
    signUpStatusForClient,
    accessStatusForClient,
  });
};

export const EventSlot = ({
  eventRef,
}: {
  eventRef: EventSlot_eventSession$key;
}) => {
  const rawFragmentEventSession = useFragment(
    EventSlotEventSessionFragmentString,
    eventRef
  );

  /**
   * Events are compiled at the top of the agenda and stored in an atom.
   * We then query them by ID here. This prevents us from overfetching
   * a bunch of event data on every single slot via recursive GQL queries.
   *
   * We only get the event session ID from the timeslot fragment here in order
   * to relate everything. This results in minimal GQL payload.
   */
  const eventFragmentKey = useAtomValue(
    getAgendaEventNodeKeyBySessionIdAtom(rawFragmentEventSession.id)
  );
  const event = useFragment(EventSlotEventFragmentString, eventFragmentKey);
  const eventSession = event?.eventSessions?.edges?.find(
    (s) => s?.node?.id === rawFragmentEventSession.id
  )?.node;

  const { onPress } = useLinkProps({
    screen: "EventDetail",
    params: { eventID: event?.id },
  });

  /**
   * If we for some reason didn't find an event session -- return null.
   * This should not happen.
   */
  if (!eventSession) {
    return null;
  }

  const durationMins = eventSession.durationMins ?? 0;

  const formattedStartAt = eventSession.startAt
    ? formatHHMM(eventSession.startAt)
    : null;

  const formattedEndAt = eventSession.endAt
    ? formatHHMM(eventSession.endAt)
    : null;

  const eventColor = event?.eventSeries?.color || "A";

  const isRecurring = (event?.eventSeries.events?.count ?? 0) > 1;

  const numRecurrences = event?.eventSeries.events?.count ?? 0;
  const recurrenceIndex = event?.recurrenceIndex ?? 0;

  const isMultiSession = (event?.eventSessions?.count ?? 0) > 1;
  const numSessions = event?.eventSessions?.count ?? 0;
  const eventSessionIndex =
    event?.eventSessions?.edges?.findIndex(
      (s) => s?.node?.id === eventSession.id
    ) ?? 0;
  const host = event?.hosts?.edges?.[0]?.node;

  const eventSignupWindows = event?.clientEligibleSignUpWindows;

  const opensAtWindows = eventSignupWindows?.filter((suw) => {
    return !!suw?.opensAt;
  });

  const opensAtWindow =
    !!opensAtWindows?.length && !!opensAtWindows?.[0]?.opensAt
      ? opensAtWindows?.[0]?.opensAt
      : undefined;

  const eventStatus = getEventSlotEventStatus({
    eventSignUpStatus: event?.signUpForClient?.status,
    signUpStatusForClient: event?.signUpStatusForClient,
    accessStatusForClient: event?.accessStatusForClient,
  });

  return (
    <EventCard
      onPress={onPress}
      durationMins={durationMins}
      eventStatus={eventStatus}
      spotsAvailable={event?.spotsAvailable || undefined}
      eventColor={eventColor}
      subTitle={event?.eventSeries.name ?? ""}
      bodyTitle={host ? host?.user.fullName : ""}
      times={`${formattedStartAt} - ${formattedEndAt}`}
      isMultiSession={isMultiSession}
      eventSessionIndex={eventSessionIndex}
      numSessions={numSessions}
      isReccuring={isRecurring}
      recurrenceIndex={recurrenceIndex}
      numRecurrences={numRecurrences}
      opensAt={opensAtWindow ?? undefined}
    />
  );
};

export const EventSlotEventSessionFragmentString = graphql`
  fragment EventSlot_eventSession on EventSession {
    id
  }
`;

export const EventSlotEventFragmentString = graphql`
  fragment EventSlot_event on Event {
    id
    accessStatusForClient(clientId: $memberId)
    signUpStatusForClient(clientId: $memberId)
    signUpForClient(clientId: $memberId) {
      status
    }
    accessStatus
    capacity
    spotsAvailable
    hasWaitlist
    eventSeries {
      name
      color
    }
    recurrenceIndex
    eventSessions {
      count
      edges {
        node {
          id
          durationMins
          name
          startAt
          endAt
        }
      }
    }
    eventSeries {
      id
      color
      name
      events {
        count
      }
    }

    hosts {
      edges {
        node {
          user {
            fullName
          }
        }
      }
    }
    clientEligibleSignUpWindows(clientId: $memberId) {
      opensAt
    }
  }
`;
